/**
 * useAssumptions — React hook encapsulating all assumption state + operations.
 *
 * Components call actions and read state; no API logic lives in components.
 * Ported from the standalone Hub's useAssumptions.js, typed, and pointed at
 * the portal proxy service (./api).
 */
import { useCallback, useState } from "react";

import {
  deleteAssumption,
  fetchAssumptionContent,
  fetchFileTemplateMappings,
  fetchTemplateContent,
  listAssumptions,
  listTemplates,
  uploadAssumption,
} from "./api";
import type {
  AssumptionContentResponse,
  AssumptionFileItem,
  FileTemplateMappings,
  GridRow,
  TemplateContent,
  TemplateItem,
} from "./types";

export interface UseAssumptions {
  // state
  files: AssumptionFileItem[];
  selectedFile: AssumptionContentResponse | null;
  loading: boolean;
  uploadProgress: number;
  error: string | null;
  successMsg: string | null;
  templates: TemplateItem[];
  activeTemplate: TemplateContent | null;
  fileTemplateMappings: FileTemplateMappings | null;
  // actions
  loadFiles: () => Promise<void>;
  fetchContent: (s3Key: string) => Promise<AssumptionContentResponse | null>;
  upload: (
    rows: GridRow[],
    fileName: string,
    templateId?: string,
    templateVersion?: string,
    originalS3Key?: string,
  ) => Promise<void>;
  remove: (s3Key: string) => Promise<void>;
  loadTemplates: () => Promise<void>;
  fetchTemplate: (s3Key: string) => Promise<TemplateContent | null>;
  setActiveTemplate: (t: TemplateContent | null) => void;
  loadFileTemplateMappings: () => Promise<void>;
}

export default function useAssumptions(): UseAssumptions {
  const [files, setFiles] = useState<AssumptionFileItem[]>([]);
  const [selectedFile, setSelectedFile] =
    useState<AssumptionContentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<TemplateContent | null>(
    null,
  );
  const [fileTemplateMappings, setFileTemplateMappings] =
    useState<FileTemplateMappings | null>(null);

  const clearMessages = () => {
    setError(null);
    setSuccessMsg(null);
  };

  const loadFiles = useCallback(async () => {
    clearMessages();
    setLoading(true);
    try {
      const result = await listAssumptions();
      const mapped: AssumptionFileItem[] = (result.files || []).map((f) => ({
        id: f.s3Key || f.fileId,
        name: f.fileName,
        date: new Date(f.lastModified).toISOString().split("T")[0],
        size: f.size,
      }));
      setFiles(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContent = useCallback(
    async (s3Key: string): Promise<AssumptionContentResponse | null> => {
      clearMessages();
      setLoading(true);
      setSelectedFile(null);
      try {
        const result = await fetchAssumptionContent(s3Key);
        setSelectedFile(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const upload = useCallback(
    async (
      rows: GridRow[],
      fileName: string,
      templateId = "",
      templateVersion = "1.0",
      originalS3Key = "",
    ) => {
      if (!rows || rows.length === 0) return;
      clearMessages();
      setLoading(true);
      setUploadProgress(0);
      try {
        const result = await uploadAssumption(
          rows,
          fileName,
          templateId,
          templateVersion,
          originalS3Key,
        );
        setSuccessMsg(result.message);
        await loadFiles();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [loadFiles],
  );

  const remove = useCallback(
    async (s3Key: string) => {
      clearMessages();
      setLoading(true);
      try {
        const result = await deleteAssumption(s3Key);
        setSuccessMsg(result.message);
        if (
          selectedFile?.s3Key === s3Key ||
          selectedFile?.s3_key === s3Key
        ) {
          setSelectedFile(null);
        }
        await loadFiles();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [loadFiles, selectedFile],
  );

  const loadTemplates = useCallback(async () => {
    try {
      const result = await listTemplates();
      const mapped: TemplateItem[] = (result.files || []).map((f) => ({
        id: f.s3Key || f.fileId,
        name: f.fileName,
        displayName: f.fileName.replace(/_/g, " ").replace(".json", ""),
      }));
      setTemplates(mapped);
    } catch (err) {
      console.error("[useAssumptions] Failed to load templates:", err);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadFileTemplateMappings = useCallback(async () => {
    try {
      const result = await fetchFileTemplateMappings();
      setFileTemplateMappings(result);
    } catch (err) {
      // Missing mapping file (fresh system) or parse issue — non-fatal.
      // Leave mappings null; the UI degrades gracefully (no template badges,
      // view->edit falls back to no template config).
      console.error("[useAssumptions] Failed to load mappings:", err);
    }
  }, []);

  const fetchTemplate = useCallback(
    async (s3Key: string): Promise<TemplateContent | null> => {
      try {
        const result = await fetchTemplateContent(s3Key);
        const templateData = result.data || (result as unknown as TemplateContent);
        setActiveTemplate(templateData);
        return templateData;
      } catch (err) {
        console.error("[useAssumptions] Failed to fetch template content:", err);
        setError(err instanceof Error ? err.message : String(err));
        return null;
      }
    },
    [],
  );

  return {
    files,
    selectedFile,
    loading,
    uploadProgress,
    error,
    successMsg,
    templates,
    activeTemplate,
    fileTemplateMappings,
    loadFiles,
    fetchContent,
    upload,
    remove,
    loadTemplates,
    fetchTemplate,
    setActiveTemplate,
    loadFileTemplateMappings,
  };
}
